import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/auth';

export default function EmailVerificationBanner() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error' | 'cooldown'>('idle');

  if (!user || user.emailVerified) return null;

  const handleResend = async () => {
    setResendStatus('sending');
    try {
      await authAPI.resendVerification(user.email);
      setResendStatus('sent');
    } catch (err) {
      const message = (err as Error).message || '';
      if (message.includes('5 minut') || message.includes('minutes') || message.includes('cooldown')) {
        setResendStatus('cooldown');
      } else {
        setResendStatus('error');
      }
    }
  };

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
      <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
          {t('auth.verifyEmail.banner')}
        </p>
        <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">
          {t('auth.verifyEmail.bannerDetail')}
        </p>
        {resendStatus === 'sent' && (
          <p className="text-sm text-green-600 dark:text-green-400 mt-1">
            {t('auth.verifyEmail.resendSuccess')}
          </p>
        )}
        {resendStatus === 'cooldown' && (
          <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
            {t('auth.verifyEmail.resendCooldown')}
          </p>
        )}
        {resendStatus === 'error' && (
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
            {t('auth.verifyEmail.resendError')}
          </p>
        )}
      </div>
      <button
        onClick={handleResend}
        disabled={resendStatus === 'sending' || resendStatus === 'sent'}
        className="shrink-0 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-200 bg-amber-100 dark:bg-amber-800/50 hover:bg-amber-200 dark:hover:bg-amber-800 border border-amber-300 dark:border-amber-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {resendStatus === 'sending' ? '...' : t('auth.verifyEmail.resendButton')}
      </button>
    </div>
  );
}
